/** The activity record's header (plan §2 "Header", §3 "Header") — the booking / user
 *  header pattern on `PageHeader`:
 *
 *  - Title = what the app shows: `display_name || name` (never empty: `Activity #<id>`);
 *    the internal `name` joins the subtitle when the two differ.
 *  - Beside it, neutral outlined chips with an icon and a word: the type (one
 *    `ACTIVITY_TYPE_LABELS`, so "Attraction" reads the same everywhere) and the category
 *    when set — with the category's own icon as the app shows it (a neutral glyph when it
 *    has none, or while the image fails). Each opens its grid filter.
 *  - Subtitle: tenant · internal name · ID · identity (the custom ID in mono, or "ext ID ✓"
 *    when only the derived ext ID is set) · Google place · Ext IDs ›, each with its value
 *    actions.
 *  - Right: the Actions menu (its Danger group in Signal Red) and **Edit** (`E`) — or, in
 *    edit mode, **Close editor** (`Esc`). No manage permission → no Edit at all; editing
 *    switched off on the environment → Edit disabled, without its `E` hint, in a tab stop
 *    named "Edit — Editing is switched off" (the user record's blocked Edit), saying so in
 *    its tooltip and in the Actions menu's note. Another reason Edit cannot open yet
 *    (`editBlockedReason`) blocks it the same way, with its own words.
 *  - Below 1100 px of content (no dock column) a **Preview** button before them opens the
 *    phone preview in a drawer (`onPreview`). The right side wraps under the title at 390 px,
 *    and its buttons wrap among themselves before anything overflows the page.
 *
 *  Pure over the detail payload; the page (after the record-core lift) owns the actions'
 *  handlers. */
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { Avatar, Box, Button, Chip, type ChipProps } from '@mui/material';
import type { ReactElement } from 'react';

import { ACTIVITY_TYPE, type ActivityCategoryRef, type ActivityDetail } from '@/api/activities';
import { visuallyHidden } from '@/components/BoolText';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { categoryIconSrc } from '@/features/activities/category/record/parts/CategoryRecordHeader';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import {
  activityCategoryActions,
  activityCustomIdActions,
  activityExtIdsActions,
  activityTypeActions,
  placeIdActions,
  type CatalogCtx,
} from '@/features/catalog/catalogLinks';
import { catalogEditGate } from '@/features/catalog/editGate';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { activitiesHref } from '@/utils/gridLinks';

import { activityTitle, activityTypeLabel, internalName } from './activityNames';

const TYPE_ICON: Readonly<Record<number, ReactElement>> = {
  [ACTIVITY_TYPE.ATTRACTION]: <AccountBalanceOutlinedIcon />,
  [ACTIVITY_TYPE.THINGS_TO_DO]: <LocalActivityOutlinedIcon />,
  [ACTIVITY_TYPE.FOOD_AND_DRINK]: <RestaurantOutlinedIcon />,
  [ACTIVITY_TYPE.LODGING]: <HotelOutlinedIcon />,
  [ACTIVITY_TYPE.TRANSPORTATION]: <DirectionsBusOutlinedIcon />,
};

/** The category chip's leading icon: the category's own (decorative — the chip's word names
 *  it), DOWNSIZED as the category record's tile shows it (`categoryIconSrc`: the resize
 *  service's copy; an SVG as stored), falling back to the neutral glyph when it has none or
 *  the image fails. */
function categoryChipIcon(category: ActivityCategoryRef): Pick<ChipProps, 'avatar' | 'icon'> {
  const src = categoryIconSrc(category.icon);
  if (!src) return { icon: <CategoryOutlinedIcon data-activity-category-icon="glyph" /> };
  return {
    avatar: (
      <Avatar
        variant="rounded"
        src={src}
        alt=""
        data-activity-category-icon="image"
        sx={{ bgcolor: 'transparent', color: 'text.secondary', '& img': { objectFit: 'contain' } }}
      >
        <CategoryOutlinedIcon sx={{ fontSize: 16 }} />
      </Avatar>
    ),
  };
}

const INLINE_ICON_SX = { fontSize: 14, verticalAlign: '-2px' } as const;

export interface ActivityRecordHeaderProps {
  detail: ActivityDetail;
  /** The active page tab's label (document title). */
  section: string;
  /** `console_activities_manage`: without it there is no Edit button at all. */
  canEdit: boolean;
  /** Editing is switched off on this environment (`CONSOLE_CATALOG_EDIT_ENABLED`). */
  editingDisabled?: boolean;
  /** With `canEdit`: why Edit cannot open yet — shown disabled with these words (its tab
   *  stop, tooltip and the Actions menu's note). */
  editBlockedReason?: string | null;
  editing?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  actionGroups?: RecordActionGroup[];
  /** Opens the phone preview's drawer (below 1100 px of content); absent → no button. */
  onPreview?: () => void;
  previewOpen?: boolean;
}

export function ActivityRecordHeader({
  detail: a,
  section,
  canEdit,
  editingDisabled = false,
  editBlockedReason = null,
  editing = false,
  onEdit,
  onClose,
  actionGroups = [],
  onPreview,
  previewOpen = false,
}: ActivityRecordHeaderProps) {
  const ctx: CatalogCtx = { tenantId: a.tenant?.id ?? null };
  const title = activityTitle(a);
  const internal = internalName(a);
  const typeLabel = activityTypeLabel(a);
  const customId = a.custom_id?.trim() || null;
  const extId = a.ext_id?.trim() || null;

  const chips = (
    <>
      <ValueActions chip actions={activityTypeActions(a.type, typeLabel, ctx)}>
        <Chip
          size="small"
          variant="outlined"
          icon={TYPE_ICON[a.type] ?? <LocalActivityOutlinedIcon />}
          label={typeLabel}
          data-activity-type={a.type}
        />
      </ValueActions>
      {a.category && (
        <ValueActions chip actions={activityCategoryActions(a.category, ctx)}>
          <Chip
            size="small"
            variant="outlined"
            {...categoryChipIcon(a.category)}
            label={a.category.title || `Category #${a.category.id}`}
            data-activity-category={a.category.id}
          />
        </ValueActions>
      )}
    </>
  );

  const subtitle = (
    <DetailSubtitle
      nowrap
      parts={[
        a.tenant ? (
          <ValueActions
            actions={operatorActions(a.tenant, {
              href: activitiesHref([], a.tenant.id),
              label: 'Activities of this operator',
            })}
          >
            {a.tenant.name || a.tenant.code}
          </ValueActions>
        ) : (
          'Unassigned'
        ),
        internal && (
          <ValueActions actions={[act.copy(internal, 'Copy internal name')]}>
            Internal name: {internal}
          </ValueActions>
        ),
        <ValueActions key="id" actions={[act.copy(String(a.id), 'Copy id')]}>
          ID {a.id}
        </ValueActions>,
        customId ? (
          <ValueActions actions={activityCustomIdActions(customId, ctx)}>
            Custom ID{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {customId}
            </Box>
          </ValueActions>
        ) : (
          extId && (
            <ValueActions actions={[act.copy(extId, 'Copy ext ID')]}>
              <span data-activity-ext-id>
                ext ID <CheckIcon aria-hidden sx={INLINE_ICON_SX} />
                <Box component="span" sx={visuallyHidden}>
                  set
                </Box>
              </span>
            </ValueActions>
          )
        ),
        a.place && (
          <ValueActions actions={placeIdActions(a.place.id)}>
            <span data-activity-place>
              <SyncIcon aria-hidden sx={{ ...INLINE_ICON_SX, mr: 0.25 }} />
              Google place
            </span>
          </ValueActions>
        ),
        <ValueActions key="ext-ids" actions={activityExtIdsActions(a.id, ctx)}>
          Ext IDs ›
        </ValueActions>,
      ]}
    />
  );

  const gate = catalogEditGate(canEdit, editingDisabled, editBlockedReason);
  const actions = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 1,
        maxWidth: '100%',
        '& .MuiButton-root': { whiteSpace: 'nowrap' },
      }}
    >
      {onPreview && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhoneAndroidOutlinedIcon />}
          onClick={onPreview}
          aria-haspopup="dialog"
          aria-expanded={previewOpen}
          data-preview-open
        >
          Preview
        </Button>
      )}
      <RecordHeaderActions groups={actionGroups} editing={editing} onEdit={onEdit} onClose={onClose} {...gate} />
    </Box>
  );

  return (
    <PageHeader
      helpStory="activities"
      section={section}
      title={title}
      titleText={title}
      titleAdornment={chips}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
